import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ServiceTypes } from "src/api/adminMemo/adminmemo-types";
import { getBuildingsAsync } from "src/api/building/building-api";
import { useApiOperation } from "src/api/hooks";
import { getCsServiceItemsAsync, getVocListAsync } from "src/api/voc/voc-api";
import { CsItemModel, CsServiceItemsParams, VocQueryParams } from "src/api/voc/voc-types";
import { ContentsTitle } from "src/components";
import HistoryDetail from "src/components/histroy/HistoryDetail";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import { vocTabs } from "../voc-constants";
import BasicInfoDetail from "./components/BasicInfoDetail";
import VocAlramSetting from "./components/VocAlramSetting";

const VocDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [vocDetail, setVocDetail] = useState<CsItemModel>();
  const [buildings, setBuildings] = useState<{ id: string; buildingName: string }[]>([]);
  const { id: vocId } = useParams();

  // voc 항목 상세
  const { executeAsync: getVocItems } = useApiOperation(getVocListAsync);
  // csService 목록 api (건물 > voc탭 목록)
  const { executeAsync: getCsService } = useApiOperation(getCsServiceItemsAsync);

  // 건물 목록 api
  const { executeAsync: getBuildings } = useApiOperation(getBuildingsAsync);

  // 1. voc 항목 상세 호출
  // 2. voc항목이 등록된 건물의 voc 호출
  useEffect(() => {
    const data = {
      searchType: "id",
      keywordList: vocId,
    };
    const fetchVocList = async (params: VocQueryParams) => {
      const { data } = await getVocItems(params);
      await fetchCsService();
      setVocDetail(data.data.content[0]);
    };

    fetchVocList(data);
  }, [getVocItems]);

  // voc항목이 등록된 건물의 ids 가져오기
  const fetchCsService = useCallback(async () => {
    const param: CsServiceItemsParams = {
      searchType: "CS_ITEM_ID",
      keywordList: vocId,
      serviceType: ServiceTypes.SERVICE_BUILDING,
      sort: {
        orders: [{ property: "id", direction: "DESC" }],
      },
    };

    const { data } = await getCsService(param);
    const vocServiceIds = data.data.content.map((item) => item.serviceId);
    if (vocServiceIds.length > 0) {
      await fetchBuildings(vocServiceIds);
    }
  }, [getCsService]);

  const fetchBuildings = useCallback(
    async (ids: string[]) => {
      const buldingData = {
        page: 0,
        size: 999,
        id: ids.join(","),
      };
      const { data } = await getBuildings(buldingData);
      const buildingList = data.data.content.map((buildng) => {
        return {
          id: String(buildng.id),
          buildingName: buildng.buildingName,
        };
      });

      setBuildings(buildingList);
    },
    [getBuildings],
  );

  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
    allowDots: true,
  });

  // 현재 활성화 되어야하는 tab
  const activeTab = useMemo(() => {
    if (queryParams?.tab) {
      return vocTabs.find((tab) => tab.value === queryParams?.tab);
    }
    return vocTabs[0];
  }, [queryParams]);

  // 탭 클릭시 callback 함수
  const clickTab = useCallback(
    (tab: { value: string; label: string }) => {
      const newQueryParams = { ...queryParams };
      if (newQueryParams?.tab) {
        delete newQueryParams.tab;
      }

      newQueryParams.tab = tab.value;

      // query parameter 에 tab 추가
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [location, queryParams],
  );

  return (
    <div>
      <MetaTag subTitle={vocId} />

      <ContentsTitle
        title="VoC 항목"
        tabs={vocTabs}
        activeTab={activeTab}
        clickTab={clickTab}
        detailInfo={[{ title: "VoC 항목 ID", value: vocId || "" }]}
      />
      <div className="contents-container__scroll">
        {/* 기본정보 */}
        {activeTab?.value === "basicInfo" && <BasicInfoDetail vocDetail={vocDetail} buildings={buildings} />}
        {activeTab?.value === "alarmSetting" && <VocAlramSetting vocDetail={vocDetail} />}
        {activeTab?.value === "history" && vocId && <HistoryDetail identifier={{ csItemId: vocId }} />}
      </div>
    </div>
  );
};

export default VocDetail;
